import ChannelIcon from "components/shared/ChannelIcon";
import { CalculationMethod } from "containers/Channels";
import type { IChannel } from "containers/Channels/types";
import type { ChannelParams } from "containers/TermFormContainer";
import { showsDefaultChannelConfiguration } from "containers/TermFormContainer/utils";
import { useTranslation } from "react-i18next";

import variables from "../../../assets/styles/_variables.module.scss";
import ChannelBaseColumnsRecap from "../ChannelBaseColumnsRecap";
import ChannelsMilestonesRecap from "../ChannelsMilestonesRecap";
import FormatOverridesRecap from "../FormatOverridesRecap";
import FormulaScopeChip from "../FormulaScopeChip";
import styles from "./style.module.scss";

interface IChannelsRecap {
    /**
     * Channel being displayed (full data from store).
     */
    fullChannelData: IChannel | undefined;
    /**
     * Default formula (formatTypeId null). May be missing if only overrides were imported.
     */
    channel?: ChannelParams;
    formatOverrides?: ChannelParams[];
    formatTypeNamesById?: ReadonlyMap<number, string>;
}

// Channels section within the Term recap component.
const ChannelsRecap = ({
    fullChannelData,
    channel,
    formatOverrides = [],
    formatTypeNamesById = new Map(),
}: IChannelsRecap) => {
    const [t] = useTranslation();
    const isForfait = fullChannelData?.calculationMethod === CalculationMethod.PACKAGE;
    const showDefaultFormula = !!channel && showsDefaultChannelConfiguration(channel);
    const defaultChannel = channel ?? formatOverrides[0];
    const channelId = defaultChannel?.channelId ?? fullChannelData?.id;
    const hasFormulaRecap = showDefaultFormula || formatOverrides.length > 0;

    return (
        <div className={styles["channel-details"]} key={`channel-${fullChannelData?.id}`}>
            <div className={isForfait ? styles["channel-title-info"] : styles["channel-title"]}>
                {fullChannelData && <ChannelIcon channelKind={fullChannelData?.kind} />}
                <span style={{ color: variables["foreground"] }} id={`recap-channel-${channelId}-name`}>
                    {fullChannelData?.name}
                </span>
            </div>
            {!isForfait && hasFormulaRecap && (
                <div className={styles["channel-body"]}>
                    <div className={styles["channel-rail"]} aria-hidden />
                    <div className={styles["channel-config"]}>
                        {showDefaultFormula && (
                            <>
                                {formatOverrides.length > 0 && (
                                    <FormulaScopeChip variant="default" label={t("ConnectTitle.DefaultFormula")} />
                                )}
                                <ChannelBaseColumnsRecap
                                    channel={channel}
                                    fullChannelData={fullChannelData}
                                    hasMilestones={!!fullChannelData?.hasMilestones || channel?.data?.length > 1}
                                />
                                <ChannelsMilestonesRecap
                                    chosenChannel={channel}
                                    fullChannelData={fullChannelData}
                                    hasMilestones={channel?.data?.length > 1}
                                />
                            </>
                        )}
                        <FormatOverridesRecap
                            overrides={formatOverrides}
                            fullChannelData={fullChannelData}
                            formatTypeNamesById={formatTypeNamesById}
                        />
                    </div>
                </div>
            )}
            {isForfait && <span className={styles["channel-info"]}>{t("TermRecap.ForfaitInfo")}</span>}
        </div>
    );
};

export default ChannelsRecap;
